import React, { createContext, useContext, useState, useEffect } from 'react';
import { getApiUrl } from '../config/apiConfig';

// Authentication Context - v2.0 with localStorage
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isDemoMode, setIsDemoMode] = useState(false);

  const API = getApiUrl().replace('/api', ''); // Get base URL without /api since we'll add it in each call

  // Get session token from localStorage
  const getSessionToken = () => {
    return localStorage.getItem('session_token');
  };

  // Set session token in localStorage
  const setSessionToken = (token) => {
    if (token) {
      localStorage.setItem('session_token', token);
    } else {
      localStorage.removeItem('session_token');
    }
  };

  // Check authentication status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Auto-logout after 30 minutes of inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    let inactivityTimer;
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

    const resetTimer = () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      inactivityTimer = setTimeout(() => {
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Events that indicate user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    
    events.forEach(event => {
      window.addEventListener(event, resetTimer);
    });

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      if (inactivityTimer) {
        clearTimeout(inactivityTimer);
      }
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [isAuthenticated]);

  // Detect a session ended elsewhere (one session per user: a login on another device/browser ends this one)
  const [sessionEnded, setSessionEnded] = useState(false);
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const probe = async () => {
      const token = getSessionToken();
      try {
        const r = await fetch(`${API}/api/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: 'include',
        });
        if (!r.ok) return;
        const d = await r.json();
        if (d.authenticated === false) setSessionEnded(true);
      } catch (e) { /* network blip - try again next tick */ }
    };
    const t = setInterval(probe, 60000);
    window.addEventListener('focus', probe);
    return () => { clearInterval(t); window.removeEventListener('focus', probe); };
  }, [isAuthenticated]);

  const acknowledgeSessionEnded = () => {
    setSessionEnded(false);
    setSessionToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const checkAuthStatus = async () => {
    try {
      const token = getSessionToken();
      const headers = {
        'Content-Type': 'application/json'
      };

      // Add Authorization header if token exists
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/api/auth/me`, {
        headers,
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.authenticated) {
          setUser(data.user);
          setIsAuthenticated(true);
          setIsDemoMode(false);
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsDemoMode(data.demo_mode);
          setSessionToken(null); // Clear invalid token
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setSessionToken(null);
      }
    } catch (error) {
      console.error('Auth check error:', error);
      setUser(null);
      setIsAuthenticated(false);
      setIsDemoMode(false);
      setSessionToken(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (sessionId) => {
    try {
      const response = await fetch(`${API}/api/auth/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ session_id: sessionId })
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.user);
        setIsAuthenticated(true);
        setIsDemoMode(false);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const emailLogin = async (email, password) => {
    console.log('🔑 emailLogin function called');
    console.log('🌐 API URL:', API);
    console.log('📧 Login email:', email);
    
    try {
      console.log('📡 Making fetch request to:', `${API}/api/auth/login`);
      
      const response = await fetch(`${API}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });

      console.log('📥 Response received:', response.status, response.statusText);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Login successful, data:', data);
        
        // Store session token in localStorage
        if (data.session_token) {
          setSessionToken(data.session_token);
          console.log('💾 Session token stored');
        }
        
        setUser(data.user);
        setIsAuthenticated(true);
        setIsDemoMode(false);
        return { success: true };
      } else {
        const error = await response.json();
        console.log('❌ Login failed:', error);
        return { success: false, error: error.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('💥 emailLogin error:', error);
      return { success: false, error: error.message };
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await fetch(`${API}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password, name })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Store session token in localStorage
        if (data.session_token) {
          setSessionToken(data.session_token);
        }
        
        setUser(data.user);
        setIsAuthenticated(true);
        setIsDemoMode(false);
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Registration failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    sessionStorage.removeItem('post_login');
    try {
      const token = getSessionToken();
      console.log('[AUTH] Logout initiated at:', new Date().toISOString());
      console.log('[AUTH] Session token exists:', !!token);
      
      const headers = {
        'Content-Type': 'application/json'
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        headers,
        credentials: 'include'
      });
      
      console.log('[AUTH] Logout response status:', response.status);
      const data = await response.json();
      console.log('[AUTH] Logout response data:', data);
    } catch (error) {
      console.error('[AUTH] Logout error:', error);
    } finally {
      console.log('[AUTH] Clearing local session data');
      setSessionToken(null); // Clear token from localStorage
      setUser(null);
      setIsAuthenticated(false);
      setIsDemoMode(false);
      console.log('[AUTH] Logout complete at:', new Date().toISOString());
    }
  };

  // Calculate isAdmin as a boolean value
  const isAdmin = user && user.role === 'admin';
  // Users whose access is scoped to the water-tank module only. They
  // see the tank runner as their entire app — no pole catalog, no
  // fixtures, no admin. Driven purely by the user's role in Mongo.
  const isTankOnly = user && user.role === 'tank_only';

  const value = {
    user,
    loading,
    isAuthenticated,
    isDemoMode,
    isAdmin,
    isTankOnly,
    login,
    emailLogin,
    register,
    logout,
    checkAuthStatus
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {sessionEnded && (
        <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4" data-testid="session-ended-modal">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900">You've been signed out</h3>
            <p className="mt-2 text-sm text-slate-600">
              Your account was signed in from another browser or device, which ends this session.
              Log in again to continue. Anything not yet saved on this page may need to be redone.
            </p>
            <button onClick={acknowledgeSessionEnded} data-testid="session-ended-login-btn"
              className="mt-5 w-full py-2 rounded-md bg-blue-600 text-white font-semibold hover:bg-blue-700">
              Log in again
            </button>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
